import { useState } from "react";
import { GridRowId, GridRowModelUpdate } from "@mui/x-data-grid-pro";
import { uniq } from "underscore";
import {
	applyDataGridRowUpdates,
	DataGridRowUpdateHandler,
} from "src/components/common/dataGrid/gridModel/dataGridRowUpdates.ts";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { resolveCustomUiActionObjectId } from "src/components/common/customUiActions/customUiActionDataGrid.ts";

export type NewRowData<RowData extends object> = {
	__generated_id: string;
	isNewRow: true;
} & Partial<RowData>;

export type InlineCrudGridRowData<RowData extends object> = RowData | NewRowData<RowData>;

export type InlineCrudGridRowUpdate<RowData extends object> = Partial<InlineCrudGridRowData<RowData>> & {
	_action?: "delete";
};

export type InlineCrudGridGetRowId<RowData extends object> = (row: InlineCrudGridRowData<RowData>) => GridRowId;

export function useInlineCrudGridRows<RowData extends object>({
	rows,
	getRowId,
	updateRows: updateRowsProp,
}: {
	rows: readonly RowData[] | undefined;
	getRowId: InlineCrudGridGetRowId<RowData>;
	updateRows: DataGridRowUpdateHandler;
}) {
	const [editedRowIds, setEditedRowIds] = useState<GridRowId[]>([]);
	const [draftRowIds, setDraftRowIds] = useState<GridRowId[]>([]);

	const isDirty = editedRowIds.length > 0 || draftRowIds.length > 0;
	const localRows = (rows as InlineCrudGridRowData<RowData>[] | undefined) ?? [];

	const changedRowIds = uniq([...editedRowIds, ...draftRowIds]);

	function addLocalRow(newRow: Partial<RowData>): GridRowId {
		const newRowGeneratedId = crypto.randomUUID();
		updateLocalRows([
			{
				...newRow,
				isNewRow: true,
				__generated_id: newRowGeneratedId,
			},
		]);
		setDraftRowIds((prev) => [...prev, newRowGeneratedId]);
		return newRowGeneratedId;
	}

	function updateRows(updates: GridRowModelUpdate[]) {
		updateLocalRows(updates);
		markRowsEdited(updates.map((update) => getRowId(update as InlineCrudGridRowData<RowData>)));
	}

	function deleteRows(rowsToDelete: InlineCrudGridRowData<RowData>[]) {
		const updates = toInlineCrudGridDeleteUpdates(rowsToDelete);
		updateLocalRows(updates);
		return updates;
	}

	function replaceSavedRows(changedRows: InlineCrudGridRowData<RowData>[], savedRows: RowData[]) {
		const savedDraftRows = changedRows.filter(isNewRow);
		const deletedDraftRowIds = savedDraftRows.map((row) => getRowId(row));
		const rowUpdates = [
			...toInlineCrudGridDeleteUpdates(savedDraftRows),
			...(savedRows as GridRowModelUpdate[]),
		];
		updateLocalRows(rowUpdates);
		clearDeletedRows(deletedDraftRowIds);
		return {
			deletedDraftRowIds,
			rowUpdates,
		};
	}

	function resetDirtyState() {
		setDraftRowIds([]);
		setEditedRowIds([]);
	}

	function markRowsEdited(rowIds: GridRowId[]) {
		setEditedRowIds((prev) => uniq([...prev, ...rowIds]));
	}

	function clearDeletedRows(deletedRowIds: GridRowId[]) {
		setEditedRowIds((prev) => prev.filter((rowId) => !deletedRowIds.includes(rowId)));
		setDraftRowIds((prev) => prev.filter((rowId) => !deletedRowIds.includes(rowId)));
	}

	function updateLocalRows(updates: GridRowModelUpdate[] | InlineCrudGridRowUpdate<RowData>[]) {
		updateRowsProp(updates as GridRowModelUpdate[]);
	}

	return {
		localRows,
		editedRowIds,
		draftRowIds,
		changedRowIds,
		isDirty,
		addLocalRow,
		updateRows,
		deleteRows,
		replaceSavedRows,
		resetDirtyState,
		markRowsEdited,
		clearDeletedRows,
		updateLocalRows,
	};
}

export function applyInlineCrudGridRowUpdates<RowData extends object>(
	currentRows: InlineCrudGridRowData<RowData>[],
	updates: GridRowModelUpdate[] | InlineCrudGridRowUpdate<RowData>[],
	getRowId: InlineCrudGridGetRowId<RowData>,
): InlineCrudGridRowData<RowData>[] {
	return applyDataGridRowUpdates(
		currentRows,
		updates as GridRowModelUpdate[],
		getRowId as (row: InlineCrudGridRowData<RowData>) => GridRowId,
	);
}

export function toInlineCrudGridDeleteUpdates<RowData extends object>(
	rowsToDelete: InlineCrudGridRowData<RowData>[],
): InlineCrudGridRowUpdate<RowData>[] {
	return rowsToDelete.map((row) => ({
		...row,
		_action: "delete",
	}));
}

export function isNewRow<RowData extends object>(value: RowData | NewRowData<RowData>): value is NewRowData<RowData> {
	return (value as NewRowData<RowData>).isNewRow;
}

type RowContextMenuComponents<RowData extends object> = AavoDataGridProps<RowData>["rowContextMenuComponents"];

/**
 * Row context menu actions operate on persisted rows, so hide them while an unsaved new row is selected.
 */
export function hideRowContextMenuForNewRows<RowData extends object>(
	rowContextMenuComponents: RowContextMenuComponents<RowData>,
): RowContextMenuComponents<RowData> {
	if (rowContextMenuComponents == null) return undefined;
	return (params) => {
		if (params.allSelectedRows.some((row) => isNewRow(row))) return null;
		return rowContextMenuComponents(params);
	};
}

/**
 * Custom UI actions operate on persisted objects, so unsaved new rows do not resolve to an object ID.
 */
export function resolveCustomUiActionObjectIdForSavedRows<RowData extends object>(
	getRowId: (row: RowData) => GridRowId,
	getObjectId: ((row: RowData) => number | null) | undefined,
): (row: RowData) => number | null {
	return (row) => (isNewRow(row) ? null : resolveCustomUiActionObjectId(row, getRowId, getObjectId));
}
